import * as vars from './_variables.scss'
import { colors } from './colors'

const SPACING_FACTOR = 4

/*
  Spacing helper functions used by the exported `theme` object for runtime CSS snippets
  (historically consumed via styled-components; spacing stays on `theme` for TS callers).
*/
type SpacingHelpers = {
    margin(value1: number, value2?: number): string
    marginHorizontal(value: number): string
    marginVertical(value: number): string
    marginTop(value: number): string
    marginBottom(value: number): string
    marginLeft(value: number): string
    marginRight(value: number): string

    padding(value1: number, value2?: number): string
    paddingHorizontal(value: number): string
    paddingVertical(value: number): string
    paddingTop(value: number): string
    paddingBottom(value: number): string
    paddingLeft(value: number): string
    paddingRight(value: number): string
}

const getSpacingProps = (
    type: 'margin' | 'padding'
): Record<
    string,
    (value1: number, value2?: number) => string | ((value: number) => string)
> => ({
    [type]: (value1: number, value2: number = value1) =>
        `${type}: ${value1 * SPACING_FACTOR}px ${value2 === 0 ? 0 : value2 * SPACING_FACTOR}px;`,
    [`${type}Horizontal`]: (value: number) => `
    ${type}-left: ${value * SPACING_FACTOR}px;
    ${type}-right: ${value * SPACING_FACTOR}px;
  `,
    [`${type}Vertical`]: (value: number) => `
    ${type}-top: ${value * SPACING_FACTOR}px;
    ${type}-bottom: ${value * SPACING_FACTOR}px;
  `,
    [`${type}Top`]: (value: number) =>
        `${type}-top: ${value * SPACING_FACTOR}px;`,
    [`${type}Bottom`]: (value: number) =>
        `${type}-bottom: ${value * SPACING_FACTOR}px;`,
    [`${type}Left`]: (value: number) =>
        `${type}-left: ${value * SPACING_FACTOR}px;`,
    [`${type}Right`]: (value: number) =>
        `${type}-right: ${value * SPACING_FACTOR}px;`,
})

const BORDER_COLOR = colors.colorBorderMain

type BorderHelpers = {
    border(size?: number): string
    borderTop(size?: number): string
    borderBottom(size?: number): string
    borderLeft(size?: number): string
    borderRight(size?: number): string
}

const getBorderProps = (): Record<string, (size?: number) => string> => ({
    border: (size = 1) => `border: ${size}px solid ${BORDER_COLOR};`,
    borderTop: (size = 1) => `border-top: ${size}px solid ${BORDER_COLOR};`,
    borderBottom: (size = 1) =>
        `border-bottom: ${size}px solid ${BORDER_COLOR};`,
    borderLeft: (size = 1) => `border-left: ${size}px solid ${BORDER_COLOR};`,
    borderRight: (size = 1) => `border-right: ${size}px solid ${BORDER_COLOR};`,
})

const FONT_SIZE = {
    bodyXs: '11px',
    bodySm: '12.6px',
    bodyMd: '13px',
    bodyMain: '14px',
    bodyLg: '16px',
    bodyXl: '20px',
    bodyXxl: '25px',

    headingXs: '15px',
    headingSm: '17px',
    headingMd: '21px',
    headingLg: '27px',
    headingXl: '36px',
}

const FONT_WEIGHT = {
    extraLight: 200,
    light: 300,
    regular: 400,
    semiBold: 500,
    bold: 600,
    extraBold: 700,
}

const FONT_FAMILY = 'proxima-nova, "Noto Sans", "Noto Sans JP", sans-serif'

const LINE_HEIGHT = {
    heading: '120%',
    body: '153%',
}

// https://www.figma.com/design/dfkn35g4dBznG0wFBkhAvC/Cobalt-Design-System?node-id=3149-696&t=hcf7FuW7rlreP8qh-0
export const TYPOGRAPHY = {
    bodyXs: `
        font-size: ${FONT_SIZE.bodyXs};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyXsBold: `
        font-size: ${FONT_SIZE.bodyXs};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodySm: `
        font-size: ${FONT_SIZE.bodySm};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodySmBold: `
        font-size: ${FONT_SIZE.bodySm};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyMd: `
        font-size: ${FONT_SIZE.bodyMd};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyMdBold: `
        font-size: ${FONT_SIZE.bodyMd};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyMain: `
        font-size: ${FONT_SIZE.bodyMain};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyMainBold: `
        font-size: ${FONT_SIZE.bodyMain};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyLg: `
        font-size: ${FONT_SIZE.bodyLg};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyLgBold: `
        font-size: ${FONT_SIZE.bodyLg};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyXl: `
        font-size: ${FONT_SIZE.bodyXl};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyXlBold: `
        font-size: ${FONT_SIZE.bodyXl};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyXxl: `
        font-size: ${FONT_SIZE.bodyXxl};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    bodyXxlBold: `
        font-size: ${FONT_SIZE.bodyXxl};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.body};
        font-family: ${FONT_FAMILY};
    `,
    headingXsLight: `
        font-size: ${FONT_SIZE.headingXs};
        font-weight: ${FONT_WEIGHT.light};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingXs: `
        font-size: ${FONT_SIZE.headingXs};
        font-weight: ${FONT_WEIGHT.semiBold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingXsBold: `
        font-size: ${FONT_SIZE.headingXs};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingSmLight: `
        font-size: ${FONT_SIZE.headingSm};
        font-weight: ${FONT_WEIGHT.light};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingSm: `
        font-size: ${FONT_SIZE.headingSm};
        font-weight: ${FONT_WEIGHT.semiBold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingSmBold: `
        font-size: ${FONT_SIZE.headingSm};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
    `,
    headingMdLight: `
        font-size: ${FONT_SIZE.headingMd};
        font-weight: ${FONT_WEIGHT.light};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingMd: `
        font-size: ${FONT_SIZE.headingMd};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingMdBold: `
        font-size: ${FONT_SIZE.headingMd};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingLgLight: `
        font-size: ${FONT_SIZE.headingLg};
        font-weight: ${FONT_WEIGHT.light};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingLg: `
        font-size: ${FONT_SIZE.headingLg};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingLgBold: `
        font-size: ${FONT_SIZE.headingLg};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingXlLight: `
        font-size: ${FONT_SIZE.headingXl};
        font-weight: ${FONT_WEIGHT.light};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingXl: `
        font-size: ${FONT_SIZE.headingXl};
        font-weight: ${FONT_WEIGHT.regular};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
    headingXlBold: `
        font-size: ${FONT_SIZE.headingXl};
        font-weight: ${FONT_WEIGHT.bold};
        line-height: ${LINE_HEIGHT.heading};
        font-family: ${FONT_FAMILY};
        letter-spacing: -0.01em;
    `,
}

export const themeBase = {
    colors,
    fontSize: FONT_SIZE,
    fontWeight: FONT_WEIGHT,
    shadows: {
        xl: vars.boxShadowXl,
        innerXs: vars.boxShadowInnerXs,
        // New design system shadows (to be adopted in phase 2)
        // Reference: https://www.figma.com/design/dfkn35g4dBznG0wFBkhAvC/Cobalt-Design-System?node-id=496-985&t=FHyI0RRuk5Wegr5D-0
        xSmall: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        small: '0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
        medium: '0px 4px 8px -2px rgba(0, 0, 0, 0.1),  0px 2px 4px -2px rgba(0, 0, 0, 0.06)',
        large: '0px 12px 16px -4px rgba(0, 0, 0, 0.08), 0px 4px 6px -2px rgba(0, 0, 0, 0.03)',
        xLarge: '0px 16px 24px -4px rgba(0, 0, 0, 0.08),0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
        xxLarge: '0px 18px 28px -10px rgba(0, 0, 0, 0.18)',
    },
    legacySpacing: {
        rowPadding: vars.rowPadding,
        cardPadding: vars.cardPadding,
        filterPanelPadding: vars.filterPanelPadding,
        contentHorizPadding: vars.contentHorizPadding,
        btnRightMargin: vars.btnRightMargin,
        iconHorizontalMargin: vars.iconHorizontalMargin,
        ruleSideGutters: vars.ruleSideGutters,
        ruleInnerGutters: vars.ruleInnerGutters,
        sidebarWidth: vars.sidebarWidth,
        sidebarCollapsedWidth: vars.sidebarCollapsedWidth,
        headerHeight: vars.headerHeight,
    },
    radius: {
        card: vars.borderRadiusCard,
        main: vars.borderRadiusMain,
    },
    filters: {
        defaultHeight: vars.filtersDefaultHeight,
        tallHeight: vars.filtersTallHeight,
    },
    spacing: (value: number) => `${value * SPACING_FACTOR}px`,
    // 4px * N
    // Functions to generate margin/padding CSS snippets (`margin: …`) for TS callers
    ...(getSpacingProps('margin') as SpacingHelpers),
    ...(getSpacingProps('padding') as SpacingHelpers),
    // Functions to generate border CSS snippets
    ...(getBorderProps() as unknown as BorderHelpers),
    textStyles: TYPOGRAPHY,
}

export type ThemeBase = typeof themeBase
